import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IChatInput } from '../../interfaces/AppInterfaces';

export const CHAT_FEEDBACK_TYPES = {
    LIKE: 'like',
    DISLIKE: 'dislike',
    COMMENT: 'comment'
}

export interface ChatFeedback {
    feedbackType: string;
    like?: boolean,
    dislike?: boolean,
    comment?: string,
    chatId: string,
    messageId: string;
}

interface GetFeedbackPayload {
    chatId: string,
    messageId: string
}

interface FeedBackMessage {
    [messageId: string]: ChatFeedback
}

interface ChatFeedbackState {
    [chatId: string]: FeedBackMessage
};

const initialState: ChatFeedbackState = {
};

const chatsFeedbackSlice = createSlice({
    name: 'chatsFeedback',
    initialState,
    reducers: {
        setChatFeedback: (state, action: PayloadAction<{ chatFeedback: ChatFeedback }>) => {
            const feedbackType = action.payload.chatFeedback.feedbackType;
            const chatId = action.payload.chatFeedback.chatId;
            const messageId = action.payload.chatFeedback.messageId;
            const like = action.payload.chatFeedback.like;
            const dislike = action.payload.chatFeedback.dislike;
            const comment = action.payload.chatFeedback.comment;

            const getFeedbackValue = (feedback: ChatFeedback, currentChatFeedback: ChatFeedback): object => {
                switch (feedback.feedbackType) {
                    case CHAT_FEEDBACK_TYPES.LIKE:
                    case CHAT_FEEDBACK_TYPES.DISLIKE: {
                        return {
                            like: like,
                            dislike: dislike,
                            comment: currentChatFeedback?.comment
                        }
                    }
                    case CHAT_FEEDBACK_TYPES.COMMENT: {
                        return {
                            comment: comment,
                            like: currentChatFeedback?.like,
                            dislike: currentChatFeedback?.dislike
                        }
                    }
                    default: {
                        return {}
                    }
                }
            }

            if (state[chatId]) {
                let currentChat = state[chatId];
                let currentMessage = {
                    feedbackType: feedbackType,
                    chatId: chatId,
                    messageId: messageId
                };
                if (currentChat[messageId]) {
                    currentMessage = currentChat[feedbackType];
                }
                currentChat[messageId] = {
                    ...currentMessage,
                    ...getFeedbackValue(action.payload.chatFeedback, currentChat[messageId])
                }

                state[chatId] = {
                    ...state[chatId],
                    ...currentChat
                };
            } else {
                state[chatId] = {
                    [messageId]: action.payload.chatFeedback
                }
            }
        },
        setChatFeedbacks: (state, action: PayloadAction<{ chatFeedbacks: FeedBackMessage }>) => {
            const chatFeedbacks = action.payload.chatFeedbacks;
            const firstFeedback = Object.values(chatFeedbacks)[0];
            const chatId = firstFeedback.chatId;
            state[chatId] = {};
            Object.keys(chatFeedbacks).forEach((key: string) => {
                state[chatId][key] = chatFeedbacks[key];
            });
        },
        getChatFeedback: (state, action: PayloadAction<{ params: GetFeedbackPayload }>): any => {
            if (state && state[action.payload.params.chatId] && state[action.payload.params.chatId][action.payload.params.messageId]) {
                return state[action.payload.params.chatId][action.payload.params.messageId]
            }
            return undefined
        }
    },
});

export const { setChatFeedback, getChatFeedback, setChatFeedbacks } = chatsFeedbackSlice.actions;
export default chatsFeedbackSlice.reducer;